/**
 * Which customer message the team should look at first (ADR 0222).
 *
 * The reply reader (ADR 0158) asks Jev to pick one meaning from six, and six
 * overlapping meanings split Jev's confidence: "have you came already?" is a
 * question, a request and a wish to talk all at once. The classifier lab
 * (September 2026) found that separate yes/no questions, one concrete thing
 * each, are answered confidently, and that ordering messages by the strongest
 * of them put every real request above every harmless one in its sample.
 *
 * So this module sorts rather than decides. Nothing here hides, closes or
 * dismisses a message: every customer message is listed, and the worst a
 * wrong answer can do is put a message lower down. The reply reader, its
 * reading and its to-do task are untouched.
 *
 * The questions are word for word the ones the lab tested. Changing a word is
 * a new experiment, not an edit: the ordering results do not carry over.
 *
 * `supabase/functions/rank-inbound/signals.ts` transcribes `signalsRequest`,
 * `readSignals` and `priorMessages`, because an Edge Function cannot import the
 * workspace; `supabase/test/rank-inbound-signals.test.ts` holds the two
 * together.
 */

import { OUR_TEXT_MAX, REPLY_READING_MODEL } from "./replyReading.js";

/** TypeSafe's model, as the reply reader pins it. */
export const SIGNALS_MODEL = REPLY_READING_MODEL;

/** Earlier messages sent with each one, oldest first, as tested. */
export const SIGNALS_HISTORY_MAX = 8;

/** The most of one earlier message Jev reads. */
export const SIGNALS_HISTORY_TEXT_MAX = OUR_TEXT_MAX;

const SUFFIX =
  " Judge the target message only; any other messages are context.";

/**
 * Every question asked, in the order asked.
 *
 * `update` is asked and never used. The lab asked it in every run the ordering
 * was measured on, and dropping it changes the request that was tested.
 */
export const SIGNAL_QUESTIONS = {
  request:
    "Does replies[0].reply ask the recipient to do or provide something, or ask a question?" +
    SUFFIX,
  problem:
    "Does replies[0].reply express a problem, complaint, or dissatisfaction?" +
    SUFFIX,
  update:
    "Does replies[0].reply provide new information that the roofing company needs to review or use?" +
    SUFFIX,
  details:
    "Does replies[0].reply provide or correct job details that the roofing company needs to use?" +
    SUFFIX,
  approval:
    "Does replies[0].reply approve work or confirm that an agreement has been signed?" +
    SUFFIX,
  payment: "Does replies[0].reply say that a payment has been made?" + SUFFIX,
  urgency:
    "Does replies[0].reply need the roofing company's attention today, for example a leak, damage, a safety issue, a crew visit or appointment happening today, or a deadline?" +
    SUFFIX,
} as const;

export type SignalKey = keyof typeof SIGNAL_QUESTIONS;
export const SIGNAL_KEYS = Object.keys(SIGNAL_QUESTIONS) as SignalKey[];

/** The five concrete checks the order is built from. */
export const CHECK_KEYS = [
  "request",
  "problem",
  "details",
  "approval",
  "payment",
] as const satisfies readonly SignalKey[];
export type CheckKey = (typeof CHECK_KEYS)[number];

/** Jev's yes-probability for each question. */
export type Signals = Record<SignalKey, number>;

/** One earlier message, as the lab sent it. */
export interface PriorMessage {
  speaker: "customer" | "company";
  text: string;
  sent_at: string;
}

/** The message being ranked, already through `replyText`. */
export interface SignalTarget {
  channel: "email" | "text";
  subject: string | null;
  text: string;
  /** Our last message before this one, through `replyText` at `OUR_TEXT_MAX`. */
  ours: string | null;
  prior: readonly PriorMessage[];
}

/** The body of one TypeSafe request: one message, seven yes/no questions. */
export function signalsRequest(target: SignalTarget): {
  model: string;
  state: unknown;
  questions: Record<string, unknown>;
} {
  const questions: Record<string, unknown> = {};
  for (const key of SIGNAL_KEYS) {
    questions[key] = { type: "noul", instructions: SIGNAL_QUESTIONS[key] };
  }
  return {
    model: SIGNALS_MODEL,
    state: {
      context:
        "Inbound customer emails and texts to a roofing company. Each message comes with its channel, optional subject, " +
        "the customer's own words with quoted email history removed, and the roofing company's last message before it, " +
        "which is what a short reply such as 'yes' or 'sounds good' is answering.",
      replies: [
        {
          channel: target.channel,
          subject: target.subject ?? "",
          reply: target.text,
          our_last_message: target.ours ?? "",
          prior_messages: target.prior.map((message) => ({
            speaker: message.speaker,
            text: message.text.slice(0, SIGNALS_HISTORY_TEXT_MAX),
            sent_at: message.sent_at,
          })),
        },
      ],
    },
    questions,
  };
}

/**
 * TypeSafe's answers as stored signals, or null when any one is missing or
 * not a probability. Null is "not sorted yet": never a guess, never a zero.
 */
export function readSignals(answers: unknown): Signals | null {
  const all = (answers ?? {}) as Record<string, { noul?: unknown } | undefined>;
  const out = {} as Signals;
  for (const key of SIGNAL_KEYS) {
    const value = all[key]?.noul;
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      value < 0 ||
      value > 1
    ) {
      return null;
    }
    out[key] = value;
  }
  return out;
}

/** One message in a conversation, either direction. */
export interface ConversationMessage {
  id: string;
  direction: "inbound" | "outbound";
  /** Already through `replyText`. */
  text: string;
  sent_at: string;
}

/**
 * The earlier messages sent with a target: the last eight strictly before
 * it, oldest first. The target itself and anything after it are never sent.
 */
export function priorMessages(
  conversation: readonly ConversationMessage[],
  target: { id: string; sent_at: string },
  max: number = SIGNALS_HISTORY_MAX,
): PriorMessage[] {
  return conversation
    .filter(
      (message) => message.id !== target.id && message.sent_at < target.sent_at,
    )
    .sort((a, b) => a.sent_at.localeCompare(b.sent_at))
    .slice(-max)
    .map((message) => ({
      speaker: message.direction === "inbound" ? "customer" : "company",
      text: message.text,
      sent_at: message.sent_at,
    }));
}

/* ---- the order -------------------------------------------------------- */

/** The groups, top of the list first. */
export const PRIORITY_GROUPS = [
  "urgent",
  "asked",
  "glance",
  "quiet",
  "unsorted",
] as const;
export type PriorityGroup = (typeof PRIORITY_GROUPS)[number];

export const PRIORITY_GROUP_LABELS: Record<PriorityGroup, string> = {
  urgent: "Needs attention now",
  asked: "Asked for something",
  glance: "Worth a glance",
  quiet: "Probably nothing needed",
  unsorted: "Not sorted yet",
};

/**
 * Where the groups split. Proposed from the lab's data, not chosen: which
 * messages the team sees first is Jonathon's call (ADR 0222).
 *
 * A check at or above `ASKED` is a clear yes: in the lab no message labelled
 * harmless reached 0.70 on any check, and 361 of 400 real ones did. Between
 * `GLANCE` and `ASKED` sat most of the real requests the checks were unsure
 * of, alongside some harmless ones, which is what "worth a glance" means.
 */
export const PRIORITY_THRESHOLDS = {
  asked: 0.5,
  glance: 0.3,
  urgent: 0.5,
} as const;

export const CHECK_REASONS: Record<CheckKey, string> = {
  request: "Asked for something",
  problem: "Reported a problem",
  details: "Gave job details",
  approval: "Approved or signed",
  payment: "Says they paid",
};

/** The reason when the strongest check is only a maybe. */
export const MAYBE_REASONS: Record<CheckKey, string> = {
  request: "Might be asking for something",
  problem: "Might be a problem",
  details: "Might be job details",
  approval: "Might be an approval",
  payment: "Might be a payment",
};

export interface Priority {
  group: PriorityGroup;
  /** The strongest of the five checks, 0 when not sorted. */
  score: number;
  /** Short reasons, strongest first; empty when there is nothing to say. */
  reasons: string[];
}

/** Which group a message is in, and why. */
export function messagePriority(signals: Signals | null): Priority {
  if (!signals) return { group: "unsorted", score: 0, reasons: [] };
  const score = Math.max(...CHECK_KEYS.map((key) => signals[key]));
  const reasons = CHECK_KEYS.filter(
    (key) => signals[key] >= PRIORITY_THRESHOLDS.asked,
  )
    .sort((a, b) => signals[b] - signals[a])
    .map((key) => CHECK_REASONS[key]);

  if (score >= PRIORITY_THRESHOLDS.asked) {
    if (signals.urgency >= PRIORITY_THRESHOLDS.urgent) {
      return {
        group: "urgent",
        score,
        reasons: ["Needs attention today", ...reasons],
      };
    }
    return { group: "asked", score, reasons };
  }
  if (score >= PRIORITY_THRESHOLDS.glance) {
    const strongest = CHECK_KEYS.reduce((a, b) =>
      signals[b] > signals[a] ? b : a,
    );
    return {
      group: "glance",
      score,
      reasons: [MAYBE_REASONS[strongest]],
    };
  }
  return { group: "quiet", score, reasons: [] };
}

/**
 * Top of the list first: by group, then the strongest check, then newest.
 * Returns a new array; ties keep a stable order by id.
 */
export function sortByPriority<
  T extends { id: string; sent_at: string; signals: Signals | null },
>(messages: readonly T[]): Array<T & { priority: Priority }> {
  const rank = (group: PriorityGroup) => PRIORITY_GROUPS.indexOf(group);
  return messages
    .map((message) => ({
      ...message,
      priority: messagePriority(message.signals),
    }))
    .sort(
      (a, b) =>
        rank(a.priority.group) - rank(b.priority.group) ||
        b.priority.score - a.priority.score ||
        b.sent_at.localeCompare(a.sent_at) ||
        a.id.localeCompare(b.id),
    );
}
